import { FormStyleContext } from "@common/contexts/FormStyle";
import { getTooltipAttributes } from "@common/contexts/Tooltip";
import HelpIcon from "@common/controls/Icon/Help/component";
import { type WithOnChange, type WithOptions, type WithValue } from "@common/declarations";
import withDynamicOptionsHOC from "@common/hocs/withDynamicOptions";
import withValue from "@common/hocs/withValue";
import { default as classnames } from "classnames";
import * as React from "react";
import RadioButton, { type RadioOptionItem } from "./Button";
import { getRadioButtonGroupStyle, getRadioButtonStyle } from "./lib";

import "./style.less";

export type RadioButtonGroupProps = WithOptions<RadioOptionItem> &
  WithValue<string | null> &
  WithOnChange<string> & {
    children?: (item: RadioOptionItem) => React.JSX.Element | undefined;
    className?: string;
    getItemStyle?: (index: number, length: number, activeOptionIndex: number) => React.CSSProperties | undefined;
    help?: string | null;
    hideToggles?: boolean;
    id?: string;
    ImageComponent?: React.ComponentType<{ src: string | null }>;
    isDisabled?: boolean;
    name?: string;
    onBlur?: () => void;
    onFocus?: () => void;
    orientation?: "horizonal" | "vertical";
    qa?: string;
    showLabel?: boolean;
    style?: React.CSSProperties;
    tooltip?: string | null;
    mode?: "Normal" | "Buttons";
  };

const RadioButtonGroupInner = React.forwardRef((props: RadioButtonGroupProps, ref: React.RefObject<HTMLDivElement>) => {
  const {
    qa,
    className,
    hideToggles = false,
    id,
    isDisabled,
    name,
    onBlur,
    onFocus,
    options,
    orientation,
    showLabel = true,
    help,
    value
  } = props;

  const actualValue = options.find((item) => item.value === value)?.value;

  const onChange = React.useCallback(
    (value: string) => {
      !props.isDisabled && props.onChange(value);
    },
    [props.isDisabled, props.onChange]
  );

  const activeOptionIndex = React.useMemo(() => options.findIndex((x) => x.value === value), [options, value]);
  return (
    <div
      ref={ref}
      id={id}
      className={classnames(className, "radio_button_group", orientation ?? "horizontal", {
        disabled: isDisabled,
        no_toggles: hideToggles,
        mode_buttons: props.mode === "Buttons"
      })}
      onBlur={onBlur}
      onFocus={onFocus}
      data-name={qa || name}
      style={props.style}
      {...getTooltipAttributes(props.tooltip)}
    >
      {options.map((item, i) => (
        <RadioButton
          key={item.value}
          {...item}
          addon={item.value === actualValue && props.children ? props.children(item) : undefined}
          showLabel={showLabel}
          hideToggles={props.hideToggles}
          isChecked={item.value === actualValue}
          isDisabled={props.isDisabled}
          onChange={onChange}
          ImageComponent={props.ImageComponent}
          style={props.getItemStyle && props.getItemStyle(i, options.length, activeOptionIndex)}
        />
      ))}

      {!!help && <HelpIcon tooltip={help} />}
    </div>
  );
});

const RadioButtonGroup = withValue<string | null, RadioButtonGroupProps>(RadioButtonGroupInner);
export default RadioButtonGroup;

const DynamicRadioButtonGroup = withDynamicOptionsHOC<any, RadioButtonGroupProps>(RadioButtonGroup);
export { DynamicRadioButtonGroup };

export const FormRadioButtonGroup = (props: RadioButtonGroupProps) => {
  const { fieldStyle } = React.useContext(FormStyleContext);

  const getItemStyle = React.useCallback(
    (index: number, length: number, activeOptionIndex: number) =>
      getRadioButtonStyle(index, length, activeOptionIndex, fieldStyle),
    [fieldStyle]
  );

  return <RadioButtonGroup {...props} style={getRadioButtonGroupStyle(fieldStyle)} getItemStyle={getItemStyle} />;
};
